/**
 * Google Business Profile Setup integration component
 * Handles Google My Business integration with DIY guides and remote access options
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, Star, Users, ExternalLink, Copy, CheckCircle, Wrench, Info, MoreVertical } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, SetupMethod, GoogleBusinessFormData } from '@/types/onboarding';
import IntegrationSetup from './IntegrationSetup';

// Validation schema
const googleBusinessSchema = z.object({
  google_setup_method: z.nativeEnum(SetupMethod),
  google_review_url: z.string().optional(),
}).refine((data) => {
  if (data.google_setup_method === SetupMethod.DIY) {
    // For DIY, require a non-empty valid URL
    if (!data.google_review_url || data.google_review_url.trim() === '') {
      return false;
    }
    // Check if it's a valid URL
    try {
      new URL(data.google_review_url);
      return true;
    } catch {
      return false;
    }
  }
  // Remote access doesn't require the URL
  return true;
}, {
  message: "Please enter a valid Google Review URL for DIY setup",
  path: ["google_review_url"],
});

type GoogleBusinessFormValues = z.infer<typeof googleBusinessSchema>;

const GoogleBusinessSetup: React.FC = () => {
  const {
    onboardingData,
    getSectionData,
    getSectionStatus,
    validateSection,
    updateSection,
    markSectionComplete,
    isSaving,
    isMarkingComplete,
  } = useOnboarding();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sectionStatus = getSectionStatus(OnboardingSection.GOOGLE_BUSINESS);
  const sectionData = useMemo(() => getSectionData(OnboardingSection.GOOGLE_BUSINESS), [onboardingData]);
  const currentSetupMethod = sectionData?.google_setup_method || SetupMethod.DIY;

  // Get domain data from Domain Setup section
  const domainSectionData = useMemo(() => getSectionData(OnboardingSection.DOMAIN), [onboardingData]);
  
  // Get the full ordering domain from domain setup
  const getOrderingDomain = () => {
    if (domainSectionData?.custom_sub_domain && domainSectionData?.root_domain) {
      return `https://${domainSectionData.custom_sub_domain}.${domainSectionData.root_domain}`;
    }
    return '';
  };

  const form = useForm<GoogleBusinessFormValues>({
    resolver: zodResolver(googleBusinessSchema),
    defaultValues: {
      google_setup_method: SetupMethod.DIY,
      google_review_url: '',
    },
  });

  // Load existing data
  useEffect(() => {
    if (sectionData) {
      form.reset({
        google_setup_method: sectionData.google_setup_method || SetupMethod.DIY,
        google_review_url: sectionData.google_review_url || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [sectionData, form.reset]);

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Handle setup method change
  const handleSetupMethodChange = (method: SetupMethod) => {
    form.setValue('google_setup_method', method);
    
    // Clear validation errors and set appropriate value for google_review_url
    if (method === SetupMethod.REMOTE_ACCESS) {
      form.setValue('google_review_url', '');
      form.clearErrors('google_review_url');
    }
    
    setHasUnsavedChanges(true);
  };

  // Save section data
  const handleSave = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.GOOGLE_BUSINESS, formData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving Google Business setup:', error);
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.GOOGLE_BUSINESS, formData);
      await markSectionComplete(OnboardingSection.GOOGLE_BUSINESS);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error marking Google Business setup complete:', error);
    }
  };

  // Watch form values to determine if can mark complete
  const formValues = form.watch();
  const canMarkComplete = useMemo(() => {
    if (formValues.google_setup_method === SetupMethod.DIY) {
      return !!(formValues.google_review_url && formValues.google_review_url !== '');
    }
    // Remote access can always be marked complete since the team handles the setup
    return true;
  }, [formValues.google_setup_method, formValues.google_review_url]);

  // Copyable domain URL component
  const CopyableDomainURL = ({ domain }: { domain: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(domain);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    return (
      <div className="mt-2 p-2 bg-gray-50 border rounded flex items-center justify-between">
        <code className="text-sm font-mono">{domain}</code>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleCopy}
          className="h-6 w-6 p-0 flex-shrink-0 ml-2"
        >
          {copied ? (
            <CheckCircle className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
    );
  };

  // Review management info popup component (copied from Domain Setup)
  const ReviewManagementInfoPopup = () => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-auto p-1 text-blue-600 hover:text-blue-800">
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div>
            <h4 className="font-medium">Pump'd review management helps you improve your Google Business Rating</h4>
            <p className="text-sm text-muted-foreground">Get more 5-star reviews, handle negative reviews offline, improve your SEO</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Request reviews via QR codes on receipts, Email and SMS</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Handle negative feedback before it hits the web</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Send 5-star reviewers to your Google Business Profile</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  // Custom DIY content to replace accordion-based steps
  const customDiyContent = (
    <div className="space-y-6">
      {/* DIY Info Component */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
        <Wrench className="h-5 w-5 text-blue-600 mt-1" />
        <div>
          <h4 className="font-medium text-blue-900">Do It Yourself</h4>
          <p className="text-sm text-blue-700">
            Follow our step-by-step guide to set up this integration yourself. 
            You'll have full control over the process.
          </p>
        </div>
      </div>

      {/* Step 1: Open Google Business Profile Manager */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">1</div>
          <div className="flex-1">
            <div className="text-sm font-medium">Open Google Business Profile Manager</div>
          </div>
        </div>

        <div className="ml-11">
          <p className="text-sm text-muted-foreground mb-3">
            Go to business.google.com and select 'See your profile'
          </p>
          <Button asChild>
            <a
              href="https://business.google.com/locations"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open Google Business
            </a>
          </Button>
          <Alert className="mt-3">
            <AlertDescription>
              Alternatively, access your profile through Google Search or Google Maps by searching for your business name
            </AlertDescription>
          </Alert>
          <div className="mt-3">
            <Button variant="outline" onClick={() => {
              const businessData = getSectionData('business_details');
              const searchQuery = `${businessData?.restaurant_name || ''} ${businessData?.address || ''}`.trim();
              if (searchQuery) {
                const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
                window.open(searchUrl, '_blank');
              }
            }}>
              Search for My Business
            </Button>
          </div>
        </div>
      </div>

      {/* Step 2: Click on 'Ask for reviews' */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">2</div>
          <div className="flex-1">
            <div className="text-sm font-medium">Click on 'Ask for reviews'</div>
          </div>
        </div>

        <div className="ml-11">
          <p className="text-sm text-muted-foreground">
            Find the reviews section and click on 'Ask for reviews' to get your review link
          </p>
        </div>
      </div>

      {/* Step 3: Copy and paste the Review Link */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">3</div>
          <div className="flex-1">
            <div className="text-sm font-medium">Copy and paste the Review Link</div>
          </div>
        </div>

        <div className="ml-11">
          <Form {...form}>
            <FormField
              control={form.control}
              name="google_review_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Google Review URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://g.page/r/..."
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Paste your Google review link here
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Form>
        </div>
      </div>

      {/* Step 4: Add Pump'd as the Preferred Ordering provider */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">4</div>
          <div className="flex-1">
            <div className="text-sm font-medium">Add Pump'd as the Preferred Ordering provider</div>
          </div>
        </div>

        <div className="ml-11">
          {/* Substep 1 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-brand-green text-white rounded-full flex items-center justify-center text-xs font-medium">1</div>
              <div className="flex-1">
                <h5 className="font-medium text-sm">Adding your Pump'd link to Google</h5>
              </div>
            </div>
            
            <div className="ml-9">
              <p className="text-sm text-muted-foreground">
                Once on your Business Profile;
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                → Select 'Food Ordering'
              </p>
              <p className="text-sm text-muted-foreground mt-1 pl-1">
                → 'Add a Link'
              </p>
              <p className="text-sm text-muted-foreground mt-1 pl-2">
                → Insert your Pump'd ordering link
              </p>
              {getOrderingDomain() && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">Your Pump'd ordering link:</p>
                  <CopyableDomainURL domain={getOrderingDomain()} />
                </div>
              )}
              <Alert className="mt-2">
                <AlertDescription>
                  Alternatively, if accessing your profile through Google Maps; 'Select Edit Profile' → 'Food Ordering'
                </AlertDescription>
              </Alert>
            </div>
          </div>

          {/* Substep 2 */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-brand-green text-white rounded-full flex items-center justify-center text-xs font-medium">2</div>
              <div className="flex-1">
                <h5 className="font-medium text-sm">Set Pump'd as the preferred link for Pickup and Delivery</h5>
              </div>
            </div>
            
            <div className="ml-9">
              <p className="text-sm text-muted-foreground">
                After adding Pump'd as a provider;
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                → Hover your mouse over the new link from the list of options
              </p>
              <p className="text-sm text-muted-foreground mt-1 pl-1">
                → Click the pencil icon
              </p>
              <p className="text-sm text-muted-foreground mt-1 pl-2">
                → Click 'Set as preferred'
              </p>
              <p className="text-sm text-muted-foreground mt-1 pl-3">
                → Toggle the switches to ON for both 'Preferred for pick-up' and 'Preferred for delivery'
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What happens next section */}
      <div className="space-y-4 mt-8 pt-6 border-t">
        <h4 className="font-medium">What happens next:</h4>
        
        <div className="space-y-4">
          {/* Point 1 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Your ordering page will become accessible from your Google Business Profile and we can begin building your review management page
                </span>
                <div className="flex-shrink-0">
                  <ReviewManagementInfoPopup />
                </div>
              </div>
            </div>
          </div>

          {/* Point 2 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Completed all steps? Mark this page as completed and our team will verify everything is setup perfectly. We'll be in touch soon to discuss your marketing and ensure your success
                </span>
              </div>
            </div>
          </div>

          {/* Point 3 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Wait 24-48 hours, then come back to this page to get access to your marketing dashboard
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom padding spacer */}
      <div className="pb-8"></div>
    </div>
  );

  // DIY requirements
  const diyRequirements = [
    "Existing Google Business Profile (or willingness to create one)",
    "Admin access to your Google Business Profile",
    "Updated business information (hours, phone, address)",
    "Google account associated with your business"
  ];

  // Custom Remote Access content to replace instructions and form
  const customRemoteContent = (
    <div className="space-y-6">
      {/* Remote Access Info Component */}
      <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
        <Users className="h-5 w-5 text-green-600 mt-1" />
        <div>
          <h4 className="font-medium text-green-900">Remote Access</h4>
          <p className="text-sm text-green-700">
            Our team will handle the setup for you. Just provide the necessary access and we'll optimize your Google Business Profile for online ordering.
          </p>
        </div>
      </div>

      {/* Step 1: Open Google Business Profile Manager (same as DIY) */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">1</div>
          <div className="flex-1">
            <div className="text-sm font-medium">Open Google Business Profile Manager</div>
          </div>
        </div>

        <div className="ml-11">
          <p className="text-sm text-muted-foreground mb-3">
            Go to business.google.com and select 'See your profile'
          </p>
          <Button asChild>
            <a
              href="https://business.google.com/locations"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open Google Business
            </a>
          </Button>
          <Alert className="mt-3">
            <AlertDescription>
              Alternatively, access your profile through Google Search or Google Maps by searching for your business name
            </AlertDescription>
          </Alert>
          <div className="mt-3">
            <Button variant="outline" onClick={() => {
              const businessData = getSectionData('business_details');
              const searchQuery = `${businessData?.restaurant_name || ''} ${businessData?.address || ''}`.trim();
              if (searchQuery) {
                const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
                window.open(searchUrl, '_blank');
              }
            }}>
              Search for My Business
            </Button>
          </div>
        </div>
      </div>

      {/* Step 2: Click on the ⋮ icon */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-medium">2</div>
          <div className="flex-1">
            <div className="text-sm font-medium">Click on the <MoreVertical className="h-4 w-4 inline mx-1" /> icon at the top right of the view</div>
          </div>
        </div>

        <div className="ml-11">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">→ Select 'Business Profile settings'</p>
            <p className="text-sm text-muted-foreground pl-1">→ Select 'People and Access'</p>
            <p className="text-sm text-muted-foreground pl-2">→ Add 'support@pumpd.co.nz' to the profile with manager role access</p>
          </div>
        </div>
      </div>

      {/* What happens next section */}
      <div className="space-y-4 mt-8 pt-6 border-t">
        <h4 className="font-medium">What happens next:</h4>
        
        <div className="space-y-4">
          {/* Point 1 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Our team will handle all the technical setup for you, ensuring that customers can order directly  
                </span>
              </div>
            </div>
          </div>

          {/* Point 2 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  We'll optimize your Google Business Profile and gather your Google review link
                </span>
              </div>
            </div>
          </div>

          {/* Point 3 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Your ordering page will become accessible from your Google Business Profile and we can begin building your review management page
                </span>
                <div className="flex-shrink-0">
                  <ReviewManagementInfoPopup />
                </div>
              </div>
            </div>
          </div>

          {/* Point 4 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Completed all steps? Mark this page as completed and our team will verify everything is setup perfectly. We'll be in touch soon to discuss your marketing and ensure your success
                </span>
              </div>
            </div>
          </div>
          
          {/* Point 5 */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              <CheckCircle className="h-4 w-4 stroke-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <span className="text-sm flex-1">
                  Wait 24-48 hours, then come back to this page to get access to your marketing dashboard
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom padding spacer */}
      <div className="pb-8"></div>
    </div>
  );

  // Remote access instructions (legacy - keeping for fallback)
  const remoteInstructions = [
    "Add support@pumpd.co.nz as a manager to your Google Business Profile",
    "Our team will optimize your profile for online ordering",
    "We'll update your business information and add relevant attributes",
    "Get your Google review link and integrate it with your ordering system",
    "Optional: Ongoing management and review responses"
  ];

  // Remote access form (legacy - keeping for fallback)
  const remoteFormContent = (
    <div className="space-y-4">
      <Alert>
        <MapPin className="h-4 w-4" />
        <AlertDescription>
          Our team will help optimize your Google Business Profile for online ordering and local search visibility. 
          We'll handle all the technical setup and provide ongoing support.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="bg-blue-50 p-4 rounded border">
          <h5 className="font-medium text-blue-900 mb-2">What We'll Do:</h5>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Verify and optimize your Google Business Profile</li>
            <li>• Add online ordering attributes and information</li>
            <li>• Set up review collection system</li>
          </ul>
        </div>

        <div className="bg-green-50 p-4 rounded border">
          <h5 className="font-medium text-green-900 mb-2">How to Give Us Access:</h5>
          <ol className="text-sm text-green-700 space-y-2">
            <li>1. Go to <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="underline">business.google.com</a></li>
            <li>2. Select your business and click "Users" in the sidebar</li>
            <li>3. Click "Invite new users"</li>
            <li>4. Add email: <code className="bg-green-100 px-1 rounded">support@pumpd.co.nz</code></li>
            <li>5. Set role to "Manager" and send invitation</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 border rounded">
            <Star className="h-5 w-5 text-yellow-500 mt-1" />
            <div>
              <h5 className="font-medium text-sm">Review Management</h5>
              <p className="text-xs text-muted-foreground">
                We'll help you get more positive reviews and respond professionally to all feedback.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 border rounded">
            <Users className="h-5 w-5 text-blue-500 mt-1" />
            <div>
              <h5 className="font-medium text-sm">Local SEO</h5>
              <p className="text-xs text-muted-foreground">
                Optimize your profile to appear higher in local search results.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 p-3 rounded border">
          <h5 className="font-medium text-amber-900 mb-1">Your Control</h5>
          <p className="text-sm text-amber-700">
            You maintain full ownership of your Google Business Profile. You can remove our access 
            at any time and we'll provide you with all login details and changes made.
          </p>
        </div>
      </div>
    </div>
  );

  // For Remote Access, ignore unsaved changes since no form data is required
  const effectiveHasUnsavedChanges = formValues.google_setup_method === SetupMethod.DIY ? hasUnsavedChanges : false;

  return (
    <IntegrationSetup
      title="Google My Business"
      description="Connect and optimize your Google Business Profile for online ordering"
      icon={<MapPin className="h-6 w-6 text-primary" />}
      status={sectionStatus}
      setupMethod={currentSetupMethod}
      
      customDiyContent={customDiyContent}
      customRemoteContent={customRemoteContent}
      diyRequirements={diyRequirements}
      
      remoteDescription="Our team will optimize your Google Business Profile, set up review collection, and provide ongoing management to improve your local search visibility."
      remoteInstructions={remoteInstructions}
      remoteFormContent={remoteFormContent}
      
      onSetupMethodChange={handleSetupMethodChange}
      onSave={handleSave}
      onMarkComplete={handleMarkComplete}
      
      isSaving={isSaving}
      isMarkingComplete={isMarkingComplete}
      hasUnsavedChanges={effectiveHasUnsavedChanges}
      canMarkComplete={canMarkComplete}
    />
  );
};

export default GoogleBusinessSetup;